import { useEffect, useRef, useState, type FormEvent } from "react";
import type { Workspace } from "../api";

/**
 * Topbar brand switcher: shows the active workspace and a dropdown to switch
 * between them, create a new one, or rename/delete a workspace. Create and
 * rename use inline inputs (no native prompts). All data mutations are delegated
 * to the parent (App), which owns the workspace list and the active-workspace
 * side effects (persisting the id + reloading the brand).
 */
export function WorkspaceSwitcher({
  workspaces,
  activeId,
  onSwitch,
  onCreate,
  onRename,
  onDelete,
  /** Surfaces a mutation failure through the app's toast system, matching every
   * other error path in the UI. Falls back to a native alert if omitted. */
  onError,
  /** When set, the trigger renders the richer sidebar chip (avatar + workspace
   * name + this sub-label, e.g. the signed-in user). Omit for the classic
   * name-only trigger. */
  subLabel,
  showActions = true,
}: {
  workspaces: Workspace[];
  activeId: string | null;
  onSwitch: (id: string) => void;
  onCreate: (name: string) => Promise<void>;
  onRename: (id: string, name: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onError?: (message: string) => void;
  subLabel?: string;
  /** Show inline Rename/Delete on each row. Off for the compact header trigger,
   * which is a quick switcher — managing workspaces lives on the sidebar chip. */
  showActions?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  function resetEditing() {
    setCreating(false);
    setRenamingId(null);
    setDraft("");
  }

  // Close the dropdown (and cancel any inline edit) on an outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Whenever the popup closes — outside click, the toggle button, Escape, or
  // after an action — drop any in-progress rename/create so a stale edit form
  // never reappears the next time it opens.
  useEffect(() => {
    if (!open) {
      setCreating(false);
      setRenamingId(null);
      setDraft("");
    }
  }, [open]);

  const active = workspaces.find((w) => w.id === activeId) ?? null;

  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Something went wrong.";
      if (onError) onError(message);
      else window.alert(message);
    } finally {
      setBusy(false);
    }
  };

  const submitCreate = (e: FormEvent) => {
    e.preventDefault();
    const name = draft.trim();
    if (!name) return;
    void run(async () => {
      await onCreate(name);
      resetEditing();
      setOpen(false);
    });
  };

  const submitRename = (e: FormEvent, w: Workspace) => {
    e.preventDefault();
    const name = draft.trim();
    if (!name || name === w.name) {
      resetEditing();
      return;
    }
    void run(async () => {
      await onRename(w.id, name);
      resetEditing();
    });
  };

  const remove = (w: Workspace) =>
    void run(async () => {
      if (
        !window.confirm(
          `Delete “${w.name}” and all its accounts, scheduled posts, and history? This cannot be undone.`,
        )
      )
        return;
      await onDelete(w.id);
    });

  const activeName = active?.name ?? "Workspace";
  const initial = activeName.trim().charAt(0).toUpperCase() || "B";

  return (
    <div className="ws-switcher" ref={ref}>
      {subLabel !== undefined ? (
        <button className="cc-ws-btn" onClick={() => setOpen((v) => !v)} disabled={busy}>
          <span className="cc-ws-avatar" aria-hidden="true">
            {initial}
          </span>
          <span className="cc-ws-meta">
            <span className="cc-ws-name">{activeName}</span>
            <span className="cc-ws-sub">{subLabel}</span>
          </span>
          <span className="cc-ws-caret" aria-hidden="true">
            ▾
          </span>
        </button>
      ) : (
        <button className="ws-current" onClick={() => setOpen((v) => !v)} disabled={busy}>
          <span className="ws-label">{activeName}</span>
          <span className="ws-caret">▾</span>
        </button>
      )}

      {open && (
        <div className="ws-menu">
          <ul className="ws-list">
            {workspaces.map((w) => (
              <li key={w.id} className={`ws-item${w.id === activeId ? " on" : ""}`}>
                {renamingId === w.id ? (
                  <form className="ws-edit" onSubmit={(e) => submitRename(e, w)}>
                    <input
                      autoFocus
                      value={draft}
                      maxLength={60}
                      placeholder="Workspace name"
                      onChange={(e) => setDraft(e.target.value)}
                    />
                    <button type="submit" className="btn small" disabled={busy}>
                      Save
                    </button>
                    <button type="button" className="ghost small" onClick={resetEditing}>
                      Cancel
                    </button>
                  </form>
                ) : (
                  <>
                    <button
                      className="ws-pick"
                      onClick={() => {
                        if (w.id !== activeId) onSwitch(w.id);
                        setOpen(false);
                      }}
                    >
                      <span className="ws-dot" />
                      <span className="ws-name">{w.name}</span>
                    </button>
                    {showActions && (
                      <span className="ws-actions">
                        <button
                          className="ghost small ws-rename"
                          onClick={() => {
                            setRenamingId(w.id);
                            setDraft(w.name);
                            setCreating(false);
                          }}
                        >
                          Rename
                        </button>
                        {workspaces.length > 1 && (
                          <button className="ghost small ws-del" onClick={() => remove(w)}>
                            Delete
                          </button>
                        )}
                      </span>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>

          {creating ? (
            <form className="ws-edit" onSubmit={submitCreate}>
              <input
                autoFocus
                value={draft}
                maxLength={60}
                placeholder="New workspace name"
                onChange={(e) => setDraft(e.target.value)}
              />
              <button type="submit" className="btn small" disabled={busy}>
                Add
              </button>
              <button type="button" className="ghost small" onClick={resetEditing}>
                Cancel
              </button>
            </form>
          ) : (
            <button
              className="ws-new"
              disabled={busy}
              onClick={() => {
                setCreating(true);
                setRenamingId(null);
                setDraft("");
              }}
            >
              + New workspace…
            </button>
          )}
        </div>
      )}
    </div>
  );
}
